import React, {
	PropsWithChildren,
	createContext,
	memo,
	useCallback,
	useContext,
	useLayoutEffect,
	useMemo,
	useState,
	useEffect,
} from "react";
import { uniq } from "lodash";
import { jsPDF } from "jspdf";

import { AdditionalTariff, Language } from "../../services";
import { useTypedSelector } from "../../redux/store";
import { callAddFont } from "../../utils";
import { DateFns } from "../../utils/DateFns";
import {
	createObjectFromConstant,
	createObjectLanguageNamesFromObject,
	languages,
	ValueLanguage,
} from "../../assets/languages/langs";
import { BaseModelsLoader } from "../../components";
import { useModelSubscribe } from "../../hooks";
import { defaultCountryCodePhone } from "../../utils/validatePhone";

jsPDF.API.events.push(["addFonts", callAddFont]);

declare module "jspdf" {
	interface jsPDF {
		lastAutoTable: {
			finalY: number;
		};
	}
}

export const AppContext = createContext<AppProvider.Context | null>(null);

export const useAppContext = (): AppProvider.Context => {
	const store = useContext<AppProvider.Context | null>(AppContext);
	if (!store) {
		throw new Error("Missing AppContext.Provider in the tree");
	}
	return store;
};

export const AppProvider: React.FC<AppProvider.Props> = ({
	children,
}): JSX.Element => {
	const { connected, authorized } = useTypedSelector((state) => state.prpc);
	const taxiServices = useTypedSelector((state) => state.taxiServices.models);
	const language = useTypedSelector((state) => state.session.language);
	const [languageExclude, setLanguageExclude] = useState<Language[] | null>(
		null,
	);
	const [countryTaxiService, setCountryTaxiService] = useState<Language>(
		defaultCountryCodePhone,
	);
	const serverTimezone = useTypedSelector(
		(state) => state.applicationConfig.timezone.name,
	);
	const defaultTaxiServiceId = useTypedSelector(
		(state) => state.session.defaultTaxiServiceId,
	);

	const canSubscribe: boolean = useMemo(
		() => connected && authorized,
		[connected, authorized],
	);

	const AdditionalTariffSubscribe = useModelSubscribe({}, AdditionalTariff, {
		debounce: 300,
		isAuth: true,
	});

	const currencyGlobalSettings = useMemo(() => {
		const defaultView = "";

		if (!canSubscribe) return defaultView;

		const models = AdditionalTariffSubscribe.models ?? [];

		if (models.length === 0) return defaultView;

		const firstModel = models[0];
		const currencySettings = firstModel?.currency?.settings;
		const selectedCurrencyStyle =
			firstModel?.additionalFields?.general?.currency?.displayStyle;

		if (!currencySettings || !selectedCurrencyStyle) return defaultView;

		const currencyName =
			currencySettings[selectedCurrencyStyle] || defaultView;

		return currencyName;
	}, [AdditionalTariffSubscribe.models, canSubscribe]);

	const [taxiServiceIdsByCompanyId, setTaxiServiceIdsByCompanyId] = useState<
		Map<number, number[]>
	>(new Map());
	const [companyIdByTaxiServiceId, setCompanyIdByTaxiServiceId] = useState<
		Map<number, number>
	>(new Map());
	const [companyNameById, setCompanyNameById] = useState<
		Map<number, Record<Language, string>>
	>(new Map());
	const [taxiServiceNameById, setTaxiServiceNameById] = useState<
		Map<number, Record<Language, string>>
	>(new Map());
	const [taxiServiceLanguagesById, setTaxiServiceLanguagesById] = useState<
		Map<number, Language[]>
	>(new Map());

	useLayoutEffect(() => {
		if (taxiServices.length) {
			taxiServices.forEach(({ id, company, settlement, settings }) => {
				if (company?.id) {
					companyIdByTaxiServiceId.set(id, company.id);
					taxiServiceNameById.set(
						id,
						createObjectLanguageNamesFromObject(settlement),
					);
					companyNameById.set(
						company?.id,
						createObjectLanguageNamesFromObject(company?.name),
					);
					taxiServiceLanguagesById.set(id, settings?.languages || []);

					if (defaultTaxiServiceId && defaultTaxiServiceId === id) {
						const country =
							settings?.map?.general?.country ||
							settings?.defaults?.mapSettings?.general?.country;
						if (country) {
							setCountryTaxiService(country);
						}
						if (settings?.languages?.length) {
							setLanguageExclude(settings.languages);
						}
					}

					const exist = taxiServiceIdsByCompanyId.get(company?.id);
					if (exist) {
						taxiServiceIdsByCompanyId.set(
							company?.id,
							uniq([...exist, id]),
						);
					} else {
						taxiServiceIdsByCompanyId.set(company?.id, [id]);
					}
				}
			});
		} else {
			setTaxiServiceNameById(new Map());
			setCompanyNameById(new Map());
			setTaxiServiceIdsByCompanyId(new Map());
			setCompanyIdByTaxiServiceId(new Map());
			setTaxiServiceLanguagesById(new Map());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [taxiServices]);

	const getCompanyName = useCallback(
		(id?: number) => {
			if (!id) return createObjectFromConstant();
			const payload = companyNameById?.get(id);
			return payload || createObjectFromConstant();
		},
		[companyNameById],
	);
	const getTaxiServiceName = useCallback(
		(id?: number) => {
			if (!id) return createObjectFromConstant();
			const payload = taxiServiceNameById?.get(id);
			return payload || createObjectFromConstant();
		},
		[taxiServiceNameById],
	);
	const getTaxiServiceLanguages = useCallback(
		(id?: number) => {
			if (!id) return [];
			const payload = taxiServiceLanguagesById?.get(id);
			return payload || [];
		},
		[taxiServiceLanguagesById],
	);
	const getTaxiServiceIds = useCallback(
		(companyId?: number) => {
			if (!companyId) return [];
			const payload = taxiServiceIdsByCompanyId?.get(companyId);
			return payload || [];
		},
		[taxiServiceIdsByCompanyId],
	);
	const getCompanyIdByTaxiServiceId = useCallback(
		(taxiServiceId?: number) => {
			if (!taxiServiceId) return undefined;
			const payload = companyIdByTaxiServiceId?.get(taxiServiceId);
			return payload;
		},
		[companyIdByTaxiServiceId],
	);

	const dateFns = useMemo(() => {
		const fns = new DateFns({ lang: language, timezone: serverTimezone });
		fns.setTimeZone(serverTimezone);
		fns.setLang(language);
		return fns;
	}, [language, serverTimezone]);

	useEffect(() => {
		if (!defaultTaxiServiceId) return;

		let intervalId: NodeJS.Timer;
		let isLoad = false;
		const updateLanguages = async () => {
			const existLanguages =
				getTaxiServiceLanguages(defaultTaxiServiceId);

			if (existLanguages.length > 0) {
				const excluded = languages.filter(
					(lang) => !existLanguages.includes(lang),
				);

				clearInterval(intervalId);
				isLoad = true;
				setLanguageExclude(excluded);
			}
		};

		if (languageExclude === null) updateLanguages();

		if (!isLoad && languageExclude === null) {
			intervalId = setInterval(updateLanguages, 200);
			// eslint-disable-next-line consistent-return
			return () => clearInterval(intervalId);
		}
	}, [defaultTaxiServiceId, getTaxiServiceLanguages, languageExclude]);

	const getLanguages = useCallback(() => {
		if (!defaultTaxiServiceId) return [];
		const existLanguages = getTaxiServiceLanguages(defaultTaxiServiceId);
		setLanguageExclude(existLanguages);

		return existLanguages;
	}, [defaultTaxiServiceId, getTaxiServiceLanguages]);

	const provideValue = useMemo(
		() => ({
			getCompanyName,
			getTaxiServiceName,
			getTaxiServiceIds,
			getCompanyIdByTaxiServiceId,
			getTaxiServiceLanguages,
			dateFns,
			serverTimezone,
			language,
			currencyGlobalSettings,
			languageExclude: languageExclude || [],
			getLanguages,
			countryTaxiService,
		}),
		[
			getCompanyName,
			getTaxiServiceIds,
			getTaxiServiceName,
			getTaxiServiceLanguages,
			getCompanyIdByTaxiServiceId,
			dateFns,
			serverTimezone,
			language,
			currencyGlobalSettings,
			languageExclude,
			getLanguages,
			countryTaxiService,
		],
	);

	return (
		<AppContext.Provider value={provideValue}>
			{canSubscribe && <BaseModelsLoader />}
			{children}
		</AppContext.Provider>
	);
};

declare namespace AppProvider {
	export interface Props extends PropsWithChildren {}

	interface Context {
		/** get company name by id */
		getCompanyName: (id?: number) => Record<Language, string>;
		/** get taxi service name by id */
		getTaxiServiceName: (id?: number) => Record<Language, string>;
		/** get taxi service languages by id */
		getTaxiServiceLanguages: (id?: number) => Language[];
		/** get taxi service ids by company id */
		getTaxiServiceIds: (companyId?: number) => number[];
		/** get company id by taxi service id */
		getCompanyIdByTaxiServiceId: (
			taxiServiceId?: number,
		) => number | undefined;
		/** get taxi service languages by id */
		getLanguages: (id?: number) => Language[];
		dateFns: DateFns;
		serverTimezone: string;
		language: ValueLanguage;
		languageExclude: ValueLanguage[];
		countryTaxiService: ValueLanguage;
		currencyGlobalSettings: string;
	}

	namespace Context {}
}

export const AppProviderMemo = memo(AppProvider);
